import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Snowflakes from './effects/Snowflakes';
import HolidayLights from './effects/HolidayLights';
import FloatingOrnaments from './effects/FloatingOrnaments';
import HolidayClock from './HolidayClock';
import HolidayGreeting from './HolidayGreeting';
import FloatingMemory from './FloatingMemory';
import MouseParallax from './effects/MouseParallax';
import { translations } from '../translations';

function Screensaver({ settings, onExit, uploadedImage, language }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const containerRef = useRef(null);
  const t = translations[language] || translations.en;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleInteraction = (e) => {
      // Small buffer to prevent instant exit if mouse just moved slightly on start
      onExit();
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart'];
    // Delay attaching listeners slightly to avoid immediate exit
    const timeout = setTimeout(() => {
        events.forEach(event => {
        document.addEventListener(event, handleInteraction);
        });
    }, 1000);

    return () => {
      clearTimeout(timeout);
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [onExit]);

  const getBackgroundClass = () => {
    switch (settings.backgroundColor) {
      case 'night':
        return 'bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#312e81]';
      case 'winter':
        return 'bg-gradient-to-br from-[#e0f2fe] via-[#bae6fd] to-[#7dd3fc]';
      case 'aurora':
        return 'bg-gradient-to-br from-[#022c22] via-[#115e59] to-[#0f172a]';
      default:
        return 'bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#312e81]';
    }
  };

  const isLightBg = settings.backgroundColor === 'winter';

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      className={`fixed inset-0 overflow-hidden cursor-none ${getBackgroundClass()}`}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <Snowflakes
          count={settings.snowflakeCount}
          speed={settings.animationSpeed}
          size={settings.particleSize}
          color={isLightBg ? 'text-blue-900/40' : 'text-white/80'}
        />
        {settings.theme === 'christmas' && (
          <MouseParallax strength={10}>
             <HolidayLights />
             <FloatingOrnaments speed={settings.animationSpeed} />
          </MouseParallax>
        )}
      </div>

      {/* Aurora Effect */}
      {settings.backgroundColor === 'aurora' && (
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute inset-0 opacity-40 blur-3xl"
            animate={{
              background: [
                'radial-gradient(circle at 20% 50%, #4ade80 0%, transparent 60%)',
                'radial-gradient(circle at 80% 50%, #60a5fa 0%, transparent 60%)',
                'radial-gradient(circle at 50% 20%, #c084fc 0%, transparent 60%)',
                'radial-gradient(circle at 20% 50%, #4ade80 0%, transparent 60%)'
              ]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center">
        <MouseParallax strength={15}>
            <div className="flex flex-col items-center justify-center h-full w-full">
                <AnimatePresence mode="wait">
                {settings.showGreeting && (
                    <HolidayGreeting theme={settings.theme} isLight={isLightBg} />
                )}
                </AnimatePresence>

                <AnimatePresence>
                {settings.showClock && (
                    <HolidayClock currentTime={currentTime} theme={settings.theme} isLight={isLightBg} />
                )}
                </AnimatePresence>
            </div>
        </MouseParallax>
      </div>

      {/* Floating Memory (Replaces Snow Globe) */}
      {uploadedImage && (
        <FloatingMemory uploadedImage={uploadedImage} language={language} />
      )}

      {/* Exit Info */}
      <div className="absolute bottom-10 text-center w-full z-20">
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 1 }}
            className={`text-sm tracking-widest uppercase ${isLightBg ? 'text-slate-600/60' : 'text-white/40'}`}
        >
          {t.exitInfo}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Screensaver;